import {
    EnumDialogName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import { FC, forwardRef, useMemo } from 'react';
import { useTracking } from '../..';
import { useAnalyticsTitle } from '../use-analytics-title';

export type DialogAnalyticsProps<T> = T & {
    /**
     * Analytics dialog name
     */
    trackDialog: EnumDialogName;
    /**
     * Analytics feature name (defaults to the feature for the current route)
     */
    trackFeature?: EnumFeature;
    /**
     * Analytics screen name (defaults to screen for the current route)
     */
    trackScreen?: EnumScreenName;
    /**
     * Any additional props
     */
    trackProps?: EventProperties;
};

const viewDialogSuffix = ' : Opened';

export function withDialogAnalytics<
    T extends {
        onOpen?: () => void;
        onUnmount?: () => void;
    }
>(Component: FC<T>) {
    function AnalyticsDialog(
        props: DialogAnalyticsProps<T> & { forwardedRef: any }
    ) {
        const {
            forwardedRef,
            trackDialog,
            trackScreen,
            trackFeature,
            trackProps,
            onOpen,
            onUnmount,
            ...others
        } = props;
        const {
            track,
            state: {
                dialog: { onDialogOpen, onDialogClose },
                currentFeature,
                currentScreenName,
            },
        } = useTracking();
        const handleOnOpen = () => {
            const screenName = trackScreen ?? currentScreenName;
            track(`${screenName} : ${trackDialog}${viewDialogSuffix}`, {
                DialogName: trackDialog!,
                Feature: trackFeature ?? currentFeature,
                ScreenName: screenName,
                IsDialogView: true,
                ...(trackProps ?? {}),
            });
            onDialogOpen(trackDialog);
            onOpen?.();
        };

        const handleOnClose = () => {
            onDialogClose(trackDialog);
            onUnmount?.();
        };

        const { analyticsTitleProps } = useAnalyticsTitle(
            `${trackDialog}  |  Dialog${trackDialog}`
        );
        return useMemo(
            () => (
                <Component
                    data-testid={`DIALOG_${trackDialog}`}
                    {...(others as typeof props)}
                    {...analyticsTitleProps}
                    ref={forwardedRef}
                    onOpen={handleOnOpen}
                    onUnmount={handleOnClose}
                />
            ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [forwardedRef, others, analyticsTitleProps, trackDialog]
        );
    }

    /**
     * HOC components do not forward `ref` by default so we setup the `forwardedRef` prop
     * and pass it to the nested function component and map it back to `ref`
     */
    // TODO: EVE-6055 figure out the props mismatch
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return forwardRef(function ForwardedAnalyticsDialog(
        props: DialogAnalyticsProps<T>,
        ref
    ) {
        return <AnalyticsDialog {...props} forwardedRef={ref} />;
    });
}
